import React, { useCallback, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { useDropzone } from 'react-dropzone';
import styled, { css } from 'styled-components';
import { ApiClient } from '../../../api/ApiClient';
import { dataUrlToBlob, useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { useSubmitter } from '../../../platform/Hook';
import { Colors, remInPx } from '../../../ui';
import { ErrorInfo, ResultInfoForSubmitter } from '../../../ui/ResultInfo';
import { Slider } from '../../../ui/Slider';
import { TextButton } from '../../../ui/TextButton';
import { AccountModel } from '../../account/AccountModel';
import { useResolve } from '../../Container';
import { DisableFrame, Flexer } from '../../dashboard/elements/LayoutElements';

const XAvatarEditor = AvatarEditor as any;

const getColor = (props) => {
    if (props.isEditActive || props.isDragAccept || props.isDragActive) {
        return Colors.primary;
    }
    if (props.isDragReject) {
        return Colors.negative;
    }
    return Colors.gray300;
};

const Frame = styled.div<{ isEditActive: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 4rem;
    height: 4rem;
    overflow: hidden;
    box-shadow: 0 0.0625rem .125rem 0 rgb(0 0 0 / 5%);
    background-size: cover;
    
    ${p => p.isEditActive ? '' : css`
        &:hover {
            cursor: pointer;
            &:after {
                content: 'Edit';
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                inset: 0;
                background: rgba(0,0,0,.5);
                color: whitesmoke;
            }
        }
    `}
    
    svg {
        width: 8rem;
        height: 8rem;
        color: #d2d6dc;
    }
`;

const DropSpot = styled.div<{ isEditActive: boolean }>`
    position: relative;
    width: 4rem;
    height: 4rem;
    border-radius: .5rem;

    &:after {
        content: '';
        position: absolute;
        inset: -0.125rem;
        border: solid 0.125rem ${props => getColor(props)};
        border-radius: .5rem;
        pointer-events: none;
    }
`;

export const ManageAccountImageView = React.memo(() => {

    // This component is pretty weirdly arranged because mostly trying to make it work with the third party component for doing the
    // cropping and drag drop at the same time.

    // It's also copied from ProfilePhotoView.tsx

    const api = useResolve(ApiClient);
    const account = useObserver(useResolve(AccountModel));

    const editor = useRef<any>();
    const [value, setValue] = useState(account.imageUrl);
    const [dropError, setDropError] = useState(null as Error | null);

    const onCancel = () => {
        setValue(account.imageUrl);
        setFile(null);
    };

    const [onSave, working, result] = useSubmitter(async () => {
        await api.upload('/admin.general.setImage', dataUrlToBlob(value));
        setFile(null);
    }, [value]);

    const [file, setFile] = useState(null as File);
    const [zoom, setZoom] = useState(1);

    const onDrop = ([ff]: File[]) => {
        if (['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'].includes(ff.type)) {
            setFile(ff);
        }
        else {
            setDropError(new Error('Please upload a jpg, png, webp or svg image file'));
        }
    };

    const onImageChange = useCallback(() => {
        setValue(editor.current.getImageScaledToCanvas().toDataURL());
    }, [editor.current]);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject
    } = useDropzone({
        noClick: !!file,
        maxFiles: 1,
        onDrop: files => onDrop(files),
    });

    return <Flexer flex="row" grow align="center" gap="2">
        <DropSpot isEditActive={!!file} {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
            <input {...getInputProps()} />
            <Frame isEditActive={!!file} style={{ backgroundImage: `url(${value})` }}>
                {!value && !file && <Icon src="deqo://icons/hero/photograph" />}
                {!!file && <>
                    <XAvatarEditor
                        ref={editor}
                        onImageChange={onImageChange}
                        onImageReady={onImageChange}
                        image={file}
                        width={remInPx(4)}
                        height={remInPx(4)}
                        border={0}
                        borderRadius={remInPx(5)}
                        color={[0, 0, 0, .5]} // RGBA
                        scale={1 + (zoom / 100)}
                        rotate={0}
                        style={{ opacity: 0 }}
                    />
                </>}
            </Frame>
        </DropSpot>
        {!!file &&
            <DisableFrame disabled={working}>
                <Flexer flex="column" gap=".5" width="8" margin=".5 0">
                    <Flexer >
                        <Slider
                            min={0}
                            max={100}
                            value={zoom}
                            onChange={setZoom}
                        />
                    </Flexer>
                    <Flexer flex="row" gap="1" center>
                        <TextButton onClick={onSave} color={Colors.primary}>Save</TextButton>
                        <TextButton onClick={onCancel} color={Colors.text}>Cancel</TextButton>
                    </Flexer>
                </Flexer>
            </DisableFrame>
        }
        {!file && (result || dropError) &&
            <Flexer>
                <ErrorInfo error={dropError} />
                <ResultInfoForSubmitter result={result} onSuccess='Saved' />
            </Flexer>
        }
    </Flexer>;
});
